import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { waLink, telLink, SITE } from "@/data/site";
import { WhatsAppIcon } from "@/components/site/BookingButtons";
import { Phone } from "lucide-react";

// WhatsApp booking form — composes a prefilled message, no backend
export default function BookingForm({ testId = "booking-form-section" }) {
  const [name, setName] = useState("");
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState("");
  const [notes, setNotes] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please tell us your name so we can address you.");
      return;
    }
    const inDate = range?.from ? format(range.from, "dd MMM yyyy") : "flexible";
    const outDate = range?.to ? format(range.to, "dd MMM yyyy") : "flexible";
    const msg = `Namaste! I'd like to book a stay at Vruksha Valley Eco Resort.
Name: ${name}
Check-in: ${inDate}
Check-out: ${outDate}
Guests: ${guests || "not specified"}${notes ? `\nNotes: ${notes}` : ""}`;
    toast.success("Opening WhatsApp with your enquiry…");
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <section data-testid={testId} className="bg-ivory py-12 md:py-16">
      <div className="mx-auto max-w-3xl px-5 md:px-10">
        <div className="text-center">
          <p className="mb-4 text-xs uppercase tracking-widest-2 text-brown md:text-sm">Reserve Your Stay</p>
          <h2 className="font-display text-4xl font-bold tracking-tight text-forest md:text-5xl">Book on WhatsApp</h2>
          <p className="mx-auto mt-3 max-w-md text-charcoal/70">Share your details and we&apos;ll open a prefilled WhatsApp chat — no forms lost, instant reply.</p>
        </div>

        <form onSubmit={submit} data-testid="booking-form" className="mt-10 rounded-2xl border border-charcoal/10 bg-card p-6 shadow-[0_10px_40px_rgba(26,54,38,0.06)] md:p-9">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="bf-name" className="text-charcoal">Your Name</Label>
              <Input id="bf-name" data-testid="booking-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ananya Rao" className="mt-2 h-11 bg-background" />
            </div>

            <div>
              <Label className="text-charcoal">Dates</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="booking-dates-trigger" className="mt-2 flex h-11 w-full items-center gap-3 rounded-md border border-input bg-background px-3 text-left text-sm text-charcoal/80">
                    <CalendarIcon className="h-4 w-4 text-brown" />
                    {range?.from ? (range.to ? `${format(range.from, "dd MMM")} – ${format(range.to, "dd MMM")}` : format(range.from, "dd MMM yyyy")) : <span className="text-muted-foreground">Check-in – Check-out</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={{ before: new Date() }} />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-charcoal">Guests</Label>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger data-testid="booking-guests" className="mt-2 h-11 bg-background"><SelectValue placeholder="Number of guests" /></SelectTrigger>
                <SelectContent>
                  {["1 Guest", "2 Guests", "3 Guests", "4 Guests", "5-8 Guests", "Large Group (8+)"].map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="bf-notes" className="text-charcoal">Notes (optional)</Label>
              <Textarea id="bf-notes" data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anniversary, room preference, dietary needs…" className="mt-2 bg-background" rows={3} />
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button type="submit" data-testid="booking-submit" className="inline-flex flex-1 items-center justify-center gap-2.5 rounded-full bg-[#1f8a4c] px-8 py-4 font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-[#1a7a42]">
              <WhatsAppIcon className="h-5 w-5" /> Send Enquiry on WhatsApp
            </button>
            <a href={telLink} data-testid="booking-call" className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/25 px-8 py-4 text-sm font-medium text-forest transition-all hover:-translate-y-0.5 hover:bg-forest hover:text-ivory">
              <Phone className="h-5 w-5" /> Call {SITE.phone}
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}
